import { Dispatch, SetStateAction, useState } from 'react'
import { JobPostDTO } from '../../models/post'
import { Chip, IconButton, Tooltip } from '@mui/material'
import AvatarEditor from 'react-avatar-editor'
import EditIcon from '@mui/icons-material/Edit'
import DeleteIcon from '@mui/icons-material/Delete'
import MenuDropdown, { MenuOption } from '../../components/MenuDropdown'
import CustomModal from '../../containers/Modal'
import JobPostModal from '../../components/Modal/JobPostModal'
import TextModal from '../../components/Modal/TextModal'
import {
    dateFormatter,
    isAdmin,
    translateEnumJobPeriodToString,
    lowerCaseWords,
    translateEnumFrequencyToString,
} from '../../helpers/utils'
import JobService from '../../services/JobService'
import { enqueueSnackbar } from 'notistack'
import HowToRegIcon from '@mui/icons-material/HowToReg'
import PostulationModal from '../../components/Modal/PostulationModal'
import CancelIcon from '@mui/icons-material/Cancel'
import { FetchResponse } from '../../models/shared'
import PersonRemoveIcon from '@mui/icons-material/PersonRemove'
import PeopleAltIcon from '@mui/icons-material/PeopleAlt'
import UserListModal from '../../components/Modal/UserListModal'

interface Props {
    info: JobPostDTO
    setMutate: Dispatch<SetStateAction<boolean>>
}

enum TextAction {
    DELETE = 'eliminar',
    FINISH = 'finalizar',
    DELETE_POSTULATE = 'eliminar tu postulación a',
    DEFAULT = '',
}

const JobPost = ({ info, setMutate }: Props) => {
    const [isNewPostOpen, setIsNewPostOpen] = useState<boolean>(false)
    const [isTextModalOpen, setIsTextModalOpen] = useState<boolean>(false)
    const [isPostulatePostOpen, setIsPostulatePostOpen] = useState<boolean>(false)
    const [modalText, setModalText] = useState<TextAction>(TextAction.DEFAULT)
    const [pending, setPending] = useState<boolean>(false)
    const [isApplicantsJobPostOpen, setIsApplicantsJobPostOpen] = useState<boolean>(false)

    const options: MenuOption[] = [
        {
            title: 'Editar',
            icon: (
                <IconButton className="pointer-events-none">
                    <EditIcon />
                </IconButton>
            ),
            action: () => setIsNewPostOpen(true),
        },
        {
            title: 'Eliminar',
            icon: (
                <IconButton className="pointer-events-none">
                    <DeleteIcon />
                </IconButton>
            ),
            action: () => {
                setModalText(TextAction.DELETE)
                setIsTextModalOpen(true)
            },
        },
        {
            title: 'Finalizar',
            icon: (
                <IconButton className="pointer-events-none">
                    <CancelIcon />
                </IconButton>
            ),
            action: () => {
                setModalText(TextAction.FINISH)
                setIsTextModalOpen(true)
            },
        },
    ]

    const handleModalAction = async () => {
        let response: FetchResponse
        let message: string
        const service = new JobService()
        setPending(true)

        if (modalText === TextAction.DELETE) {
            response = await service.deleteJobPost(info.id)
            message = 'Oferta Laboral eliminada'
        } else if (modalText === TextAction.FINISH) {
            response = await service.finishJobPost(info.id)
            message = 'Oferta Laboral finalizada'
        } else {
            response = await service.deletePostulate(info.id)
            message = 'Se ha eliminado la postulación'
        }

        setPending(false)

        if (response.status === 200) {
            enqueueSnackbar(`${message}`, {
                variant: 'success',
                autoHideDuration: 6000,
            })
            setMutate((prev) => !prev)
        } else {
            enqueueSnackbar('Error inesperado', {
                variant: 'error',
                autoHideDuration: 6000,
            })
        }
    }

    return (
        <>
            <div className="flex flex-col gap-2 md:gap-4">
                <div className="flex gap-2 md:gap-4 justify-between">
                    <div className="flex gap-2 md:gap-4 items-center">
                        <AvatarEditor
                            image={info.companyLogo}
                            height={40}
                            width={40}
                            border={0}
                            className="z-1 rounded-full pointer-events-none"
                        />
                        <div className="flex flex-col">
                            <span className="text-sm md:text-base">{info.company}</span>
                            {isAdmin() && (
                                <span className="text-[10px] md:text-xs">{info.contact}</span>
                            )}
                            <span className="text-[10px] md:text-xs">{info.location}</span>
                        </div>
                    </div>
                    {isAdmin() ? (
                        <div className="flex justify-end">
                            {info.applicants.length > 0 && (
                                <Tooltip
                                    title="Ver postulados"
                                    placement="left"
                                >
                                    <div>
                                        <IconButton
                                            onClick={() => setIsApplicantsJobPostOpen(true)}
                                        >
                                            <PeopleAltIcon />
                                        </IconButton>
                                    </div>
                                </Tooltip>
                            )}
                            <MenuDropdown options={options} />
                        </div>
                    ) : !info.userApplied ? (
                        <Tooltip
                            title="Postularse"
                            placement="left"
                        >
                            <div>
                                <IconButton onClick={() => setIsPostulatePostOpen(true)}>
                                    <HowToRegIcon />
                                </IconButton>
                            </div>
                        </Tooltip>
                    ) : (
                        <Tooltip
                            title="Quitar postulación"
                            placement="left"
                        >
                            <div>
                                <IconButton
                                    onClick={() => {
                                        setModalText(TextAction.DELETE_POSTULATE)
                                        setIsTextModalOpen(true)
                                    }}
                                >
                                    <PersonRemoveIcon />
                                </IconButton>
                            </div>
                        </Tooltip>
                    )}
                </div>
                <span className={`block break-words max-w-full text-xl md:text-2xl`}>
                    {info.title}
                </span>
                <span
                    className={`whitespace-pre-line block break-words max-w-full text-sm md:text-base`}
                >
                    {info.description}
                </span>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2 md:gap-4">
                    <span className={`block break-words max-w-full text-sm md:text-base`}>
                        Modalidad: {lowerCaseWords(info.modality)}
                    </span>
                    <span className={`block break-words max-w-full text-sm md:text-base`}>
                        Fecha Límite: {dateFormatter(new Date(info.finishDate))}
                    </span>
                    <span className={`block break-words max-w-full text-sm md:text-base`}>
                        Salario aproximado:{' '}
                        {!!info.salary.amount
                            ? `${info.salary.amount.toLocaleString()} ${info.salary.currency}`
                            : 'Sin especificar'}
                    </span>
                    <span className={`block break-words max-w-full text-sm md:text-base`}>
                        Jornada Laboral: {translateEnumJobPeriodToString(info.salary.period)}
                    </span>
                    <span className={`block break-words max-w-full text-sm md:text-base`}>
                        Frecuencia de Pago: {translateEnumFrequencyToString(info.salary.frequency)}
                    </span>
                </div>
                <div className="flex gap-1 items-center flex-wrap">
                    <span className={`block break-words max-w-full text-sm md:text-base`}>
                        Aptitudes deseadas:
                    </span>
                    {info.abilities.map((ability) => (
                        <div key={`${info.id}selected${ability}`}>
                            <Chip
                                label={ability}
                                className="bg-optional text-secondary font-[500]"
                            />
                        </div>
                    ))}
                </div>
            </div>
            <CustomModal
                isOpen={isNewPostOpen}
                handleClose={() => setIsNewPostOpen(false)}
            >
                <JobPostModal
                    info={{ ...info, finishDate: dateFormatter(new Date(info.finishDate)) }}
                    setMutate={setMutate}
                    handleClose={() => setIsNewPostOpen(false)}
                />
            </CustomModal>
            <CustomModal
                isOpen={isTextModalOpen}
                handleClose={() => setIsTextModalOpen(false)}
            >
                <TextModal
                    title={`${modalText.charAt(0).toUpperCase()}${modalText.slice(1)} oferta laboral`}
                    content={`¿Estás seguro de que querés ${modalText} esta oferta laboral?`}
                    onAction={() => {
                        handleModalAction()
                    }}
                    onClose={() => setIsTextModalOpen(false)}
                    loading={pending}
                />
            </CustomModal>
            <CustomModal
                isOpen={isPostulatePostOpen}
                handleClose={() => setIsPostulatePostOpen(false)}
            >
                <PostulationModal
                    postId={info.id}
                    setMutate={setMutate}
                    handleClose={() => setIsPostulatePostOpen(false)}
                />
            </CustomModal>
            <CustomModal
                isOpen={isApplicantsJobPostOpen}
                handleClose={() => setIsApplicantsJobPostOpen(false)}
            >
                <UserListModal
                    attendees={info.applicants}
                    onClose={() => setIsApplicantsJobPostOpen(false)}
                    title="Postulados"
                />
            </CustomModal>
        </>
    )
}

export default JobPost
